import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Send } from 'lucide-react';
import SlashMenu, { filterSlashCommands, SLASH_COMMANDS } from './SlashMenu';

// Multi-line composer with slash-command popover.
//
// - `/` at the start of an empty (or only-leading-slash) line opens
//   the slash menu; ArrowUp/ArrowDown move the highlight, Enter/Tab
//   pick the highlighted command, Escape dismisses the menu. The
//   picked command's template replaces the slash prefix so the user
//   can continue typing the argument.
// - Enter submits; Shift+Enter inserts a newline.
//
// Exposes `restore(text)` via ref so the parent can hand a message
// back after a failed send (the composer clears optimistically on
// submit).

const STROKE = 2.75;

const Composer = forwardRef(function Composer({ onSend, disabled, placeholder, streaming, onStop }, ref) {
  const [value, setValue] = useState('');
  const [slashDismissed, setSlashDismissed] = useState(false);
  const [slashIndex, setSlashIndex] = useState(0);
  const textareaRef = useRef(null);

  useImperativeHandle(ref, () => ({
    restore(text) {
      setValue(text);
      setTimeout(() => textareaRef.current && textareaRef.current.focus(), 0);
    },
  }), []);

  // Auto-resize the textarea up to 8 lines high.
  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = `${Math.min(ta.scrollHeight, 200)}px`;
  }, [value]);

  // Show the slash menu when the current text starts with `/` and has
  // no space yet — i.e. they're still picking a command. Typing again
  // clears an Escape-dismissal.
  const trimmed = value.trimStart();
  const slashActive = trimmed.startsWith('/') && !trimmed.includes(' ');
  useEffect(() => { setSlashDismissed(false); }, [value]);

  const slashItems = useMemo(
    () => (slashActive ? filterSlashCommands(trimmed) : []),
    [slashActive, trimmed],
  );
  const showSlash = slashActive && !slashDismissed && slashItems.length > 0;

  // Keep the highlight in range as the filter narrows.
  useEffect(() => {
    setSlashIndex(i => Math.min(i, Math.max(0, slashItems.length - 1)));
  }, [slashItems.length]);

  const submit = () => {
    const text = value.trim();
    if (!text || disabled) return;

    // If the user typed `/cmd rest`, expand it via the template so the
    // model sees a fully-formed prompt rather than a slash literal.
    let finalText = text;
    const m = text.match(/^\/(\w+)(?:\s+(.+))?$/s);
    if (m) {
      const cmd = SLASH_COMMANDS.find(c => c.cmd === `/${m[1]}`);
      if (cmd) finalText = cmd.template((m[2] || '').trim());
    }

    onSend(finalText);
    setValue('');
  };

  const pickSlash = (cmd) => {
    const rest = value.trimStart().slice(value.trimStart().indexOf(' ') + 1);
    const hasRest = value.trimStart().includes(' ');
    setValue(hasRest ? `${cmd.cmd} ${rest}` : `${cmd.cmd} `);
    setSlashIndex(0);
    setTimeout(() => textareaRef.current && textareaRef.current.focus(), 0);
  };

  const handleKey = (e) => {
    if (showSlash) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSlashIndex(i => (i + 1) % slashItems.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSlashIndex(i => (i - 1 + slashItems.length) % slashItems.length);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        pickSlash(slashItems[slashIndex] || slashItems[0]);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setSlashDismissed(true);
        return;
      }
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  return (
    <div className="chat-composer-wrap">
      {showSlash && (
        <SlashMenu
          items={slashItems}
          selectedIndex={slashIndex}
          onPick={pickSlash}
          onHover={setSlashIndex}
        />
      )}
      <div className="chat-composer">
        <textarea
          ref={textareaRef}
          rows={1}
          className="chat-composer-input"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKey}
          placeholder={placeholder || 'Ask about a brand, request a list, or run /commands…'}
          disabled={disabled}
        />
        <button
          type="button"
          className="btn btn-primary btn-icon chat-composer-send"
          onClick={submit}
          disabled={disabled || !value.trim()}
          aria-label="Send"
          title="Send"
        >
          <Send size={17} strokeWidth={STROKE} />
        </button>
      </div>
      <div className="chat-composer-hint">
        <span><kbd>Enter</kbd> send · <kbd>Shift</kbd>+<kbd>Enter</kbd> newline · type <kbd>/</kbd> for commands</span>
        {streaming && onStop && (
          <button type="button" className="btn btn-secondary chat-stop-btn" onClick={onStop}>
            Stop
          </button>
        )}
      </div>
    </div>
  );
});

export default Composer;
